<template>
    <!-- Guided BAA builder (Joe). One question per screen; method chosen first branches
         the rest. The BAA builds in a live preview BESIDE the question (side-by-side
         builder layout). Merge fields come from the chosen template's own field inputs (rendered
         as with_fields), so submission reuses the proven create path and every template
         variable gets its own question - nothing ships with a leftover %%placeholder%%. -->
    <form method="POST" :action="formAction" ref="form" class="bgw-wrap" :class="{'bgw-advmode': advancedOpen}" @submit="onFormSubmit">
        <input type="hidden" name="_token" :value="csrf">
        <input type="hidden" name="type" :value="type">
        <input type="hidden" name="template" :value="templateId">
        <input type="hidden" name="name" :value="baaName">
        <input type="hidden" name="ba_company_name" :value="baCompany">
        <input type="hidden" name="renewal_frequency" :value="reviewFreq">
        <input type="hidden" name="signed_as" value="ce">
        <input type="hidden" name="signed_as_third_party" value="ba">
        <input type="hidden" name="allow_no_password" :value="noPassword ? 1 : ''">
        <input type="hidden" name="no_password_reason" :value="noPasswordReason">
        <input type="hidden" name="password" :value="noPassword ? '' : password">
        <input type="hidden" name="associate_email[]" :value="signerEmail">
        <input type="hidden" name="baa" :value="customBody" v-if="type === 'custom'">
        <!-- P0-2: this wizard's submit IS "Review & send" - there is no later
             separate send step - so store() starts the signer workflow itself
             when it sees this flag (never set by the classic multi-step editor
             or the background autosave). -->
        <input type="hidden" name="send_on_save" value="1">
        <input type="hidden" name="submission_uuid" :value="submissionUuid">
        <input type="hidden" name="body_override" :value="bodyOverride" v-if="bodyOverrideDirty && type === 'medcurity'">

        <!-- compact high-level progress -->
        <div class="bgw-prog">
            <template v-for="(s,i) in progress">
                <div class="bgw-pdot" :class="{done: s.done, now: s.now}" :key="'d'+i">
                    <span class="bgw-pnum"><span v-if="s.done">&#10003;</span><span v-else>{{ i+1 }}</span></span>
                    <span class="bgw-plabel">{{ s.label }}</span>
                </div>
                <span v-if="i < progress.length - 1" class="bgw-pbar" :class="{done: s.done}" :key="'b'+i"></span>
            </template>
        </div>

        <!-- Round 7 (Joe req 2026-09-01): side-by-side builder layout, matching
             the standard-BAA template builder (BaaTemplateBuilder.vue .btb-split):
             questions LEFT, live document RIGHT, stacked on narrow viewports. -->
        <div class="bgw-split">
        <!-- LEFT PANE: steps / questions -->
        <div class="bgw-pane bgw-pane-form">

        <!-- QUESTION -->
        <div class="bgw-card bgw-qcard" v-show="!advancedOpen">
            <div class="bgw-count">{{ countLabel }}</div>

            <!-- Q1: method -->
            <div v-if="phase === 'method'">
                <div class="bgw-qt">How do you want to create this BAA?</div>
                <div class="bgw-qh">This choice sets which questions come next.</div>
                <div class="bgw-methods">
                    <div v-for="m in methods" :key="m.key" class="bgw-method" :class="{sel: method === m.key}" @click="selectMethod(m.key)">
                        <div class="bgw-mt">{{ m.mt }}</div>
                        <div class="bgw-md">{{ m.md }}</div>
                    </div>
                </div>
            </div>

            <!-- pick a saved/medcurity template -->
            <div v-else-if="phase === 'template'">
                <div class="bgw-qt">Which template?</div>
                <div class="bgw-qh" v-if="templateChoices.length === 0">No {{ method === 'other' ? 'other' : 'saved' }} templates yet. Templates you save from finished BAAs will appear here.</div>
                <template v-else>
                    <div class="bgw-qh" v-if="templateAutoFailed">We could not automatically pick Medcurity's standard template - choose one below.</div>
                    <div class="bgw-qh" v-else>Your answers fill it in. Every fill-in field becomes one quick question, pre-filled where we can.</div>
                    <select class="bgw-input" v-model="templateId" @change="onTemplateChosen">
                        <option value="" disabled>Select a template</option>
                        <option v-for="t in templateChoices" :key="t.id" :value="t.id">{{ t.name }}</option>
                    </select>
                </template>
            </div>

            <!-- one question per merge field -->
            <div v-else-if="phase === 'field'">
                <div class="bgw-qt">{{ currentField.label }}</div>
                <!-- Item 1 (Joe, 2026-08-14, no raw tokens): never show the
                     %%token%% itself - a per-field plain-English help sentence
                     (from FIELD_COPY) when we have one, else a generic line
                     that still says nothing about merge-field syntax. -->
                <div class="bgw-qh">{{ currentField.help || 'Your answer appears in the agreement preview beside this.' }}</div>
                <!-- Item 3 (Joe, 2026-08-14): a native <select> can't wrap its
                     selected text, so a long clause option collapsed to an
                     unreadable sliver once chosen. Real radio inputs (visually
                     hidden, full option text in the label) give every option
                     full always-visible, wrapping text and native keyboard/
                     arrow-key support, styled like the .bgw-method cards above.
                     Still writes straight into currentField.value + syncField()
                     so the inline contract select is fed exactly as before. -->
                <!-- JW3 P0-A (Joe, 2026-08-15): a template checkbox field
                     ("Select all that apply") renders server-side as a GROUP of
                     <input type=checkbox name="slug[id][]"> - extractFields used
                     to collapse that whole group to the first checkbox treated as
                     a free-text box, so the customer saw one clipped single-line
                     clause and no way to pick. Render a real multi-select of
                     checkbox cards instead: full wrapping clause text always
                     visible, keyboard-operable, screen-reader labelled, and
                     v-model straight into currentField.value (an array) so
                     syncField() checks/unchecks the underlying preview inputs and
                     the form still posts slug[id][]=<clause> exactly as the
                     server generator expects. -->
                <div v-if="currentField.checkbox" class="bgw-options" role="group" :aria-label="currentField.label" ref="fieldInput">
                    <label v-for="o in currentField.options" :key="o.value" class="bgw-check-opt" :class="{sel: currentField.value.indexOf(o.value) !== -1}">
                        <input type="checkbox" class="bgw-radio-input" :value="o.value" v-model="currentField.value" @change="syncField(currentField)">
                        <span class="bgw-check-mark" aria-hidden="true">&#10003;</span>
                        <span class="bgw-radio-text">{{ o.label }}</span>
                    </label>
                    <div class="bgw-qh" style="margin:2px 0 0;">Select all that apply, or leave every box unchecked to add none of these.</div>
                </div>
                <div v-else-if="currentField.options && currentField.options.length" class="bgw-options" role="radiogroup" :aria-label="currentField.label" ref="fieldInput">
                    <label v-for="o in currentField.options" :key="o.value" class="bgw-radio-opt" :class="{sel: currentField.value === o.value}">
                        <input type="radio" class="bgw-radio-input" :name="'bgw-opt-' + currentField.slug" :value="o.value" v-model="currentField.value" @change="syncField(currentField)">
                        <span class="bgw-radio-mark" aria-hidden="true"></span>
                        <span class="bgw-radio-text">{{ o.label }}</span>
                    </label>
                </div>
                <textarea v-else-if="currentField.multiline" class="bgw-input bgw-area" v-model="currentField.value" @input="syncField(currentField)" ref="fieldInput" :placeholder="currentField.label"></textarea>
                <input v-else class="bgw-input" type="text" v-model="currentField.value" @input="syncField(currentField)" ref="fieldInput" :placeholder="currentField.label">
                <div class="bgw-suggest" v-if="!(currentField.options && currentField.options.length) && currentField.suggest && currentField.suggest !== currentField.value">Suggested: <b>{{ currentField.suggest }}</b>
                    <span class="bgw-chip" @click="acceptSuggest(currentField)">&#10003; Use this</span>
                </div>
            </div>

            <!-- blank: title + body -->
            <div v-else-if="phase === 'title'">
                <div class="bgw-qt">Give this BAA a title.</div>
                <div class="bgw-qh">Shown at the top of the agreement. Tip: include the business associate's name.</div>
                <input class="bgw-input" type="text" v-model="title" :placeholder="titleSuggest || 'e.g. BAA for Acme Billing Services'">
                <div class="bgw-suggest" v-if="titleSuggest && titleSuggest !== title">Suggested: <b>{{ titleSuggest }}</b>
                    <span class="bgw-chip" @click="title = titleSuggest">&#10003; Use this</span></div>
            </div>
            <div v-else-if="phase === 'body'">
                <div class="bgw-qt">Write the agreement.</div>
                <div class="bgw-qh">Type or paste your language. It appears in the preview beside this as you write.</div>
                <textarea class="bgw-input bgw-area" v-model="customBody" placeholder="Write or paste your BAA language..."></textarea>
            </div>

            <!-- upload/paste -->
            <div v-else-if="phase === 'source'">
                <div class="bgw-qt">Bring in the existing BAA.</div>
                <div class="bgw-qh">Paste the text of the BAA you already have. It is kept as-is, with no fill-in step.</div>
                <textarea class="bgw-input bgw-area" v-model="customBody" placeholder="Paste the BAA text..."></textarea>
            </div>

            <!-- business associate -->
            <div v-else-if="phase === 'ba'">
                <div class="bgw-qt">Who is this BAA with?</div>
                <div class="bgw-qh">The business associate's company name.</div>
                <input class="bgw-input" type="text" v-model="baCompany" @input="syncAutoBindings" :placeholder="suggestBa || 'Business associate company name'">
                <div class="bgw-suggest" v-if="suggestBa && suggestBa !== baCompany">Suggested: <b>{{ suggestBa }}</b>
                    <span class="bgw-chip" @click="baCompany = suggestBa">&#10003; Use this</span></div>
            </div>

            <!-- signer -->
            <div v-else-if="phase === 'signer'">
                <div class="bgw-qt">Who signs for the business associate?</div>
                <div class="bgw-qh">We email this person a secure link to review and sign. It is not their Medcurity login.</div>
                <input class="bgw-input" type="email" v-model="signerEmail" placeholder="name@vendor.com">
            </div>

            <!-- signer + passcode on one card (standard streamlined send) -->
            <div v-else-if="phase === 'signerpass'">
                <div class="bgw-qt">Who signs for the business associate?</div>
                <div class="bgw-qh">We email this person a secure link to review and sign. It is not their Medcurity login.</div>
                <input class="bgw-input" type="email" v-model="signerEmail" placeholder="name@vendor.com">
                <div class="bgw-qt" style="font-size:1.05rem;margin-top:22px;">Protect the signing link with a passcode?</div>
                <div class="bgw-choices">
                    <div class="bgw-choice" :class="{sel: noPassword === false}" @click="noPassword = false"><span class="bgw-rr"></span>Yes, set a passcode</div>
                    <div class="bgw-choice" :class="{sel: noPassword === true}" @click="noPassword = true"><span class="bgw-rr"></span>No, send without one</div>
                </div>
                <input v-if="!noPassword" class="bgw-input" style="margin-top:12px;" type="text" v-model="password" placeholder="Set a passcode the signer will type to open the BAA">
                <input v-if="noPassword" class="bgw-input" style="margin-top:12px;" type="text" v-model="noPasswordReason" placeholder="Why is no passcode needed? (optional)">
            </div>

            <!-- passcode -->
            <div v-else-if="phase === 'pass'">
                <div class="bgw-qt">Protect the signing link with a passcode?</div>
                <div class="bgw-qh">A short passcode the signer types to open the BAA. Skip it if they can't use one.</div>
                <div class="bgw-choices">
                    <div class="bgw-choice" :class="{sel: noPassword === false}" @click="noPassword = false"><span class="bgw-rr"></span>Yes, set a passcode</div>
                    <div class="bgw-choice" :class="{sel: noPassword === true}" @click="noPassword = true"><span class="bgw-rr"></span>No, send without one</div>
                </div>
                <input v-if="!noPassword" class="bgw-input" style="margin-top:12px;" type="text" v-model="password" placeholder="Set a passcode the signer will type to open the BAA">
                <input v-if="noPassword" class="bgw-input" style="margin-top:12px;" type="text" v-model="noPasswordReason" placeholder="Why is no passcode needed? (optional)">
            </div>

            <!-- review frequency -->
            <div v-else-if="phase === 'renew'">
                <div class="bgw-qt">How often should this BAA be reviewed?</div>
                <div class="bgw-choices">
                    <div class="bgw-choice" v-for="(lab,val) in renewFrequencies" :key="val" :class="{sel: reviewFreq == val}" @click="reviewFreq = val"><span class="bgw-rr"></span>{{ lab }}</div>
                </div>
            </div>

            <!-- JW3 P0-B (Joe, 2026-08-15): a genuine Step-6 Review summary. The
                 wizard's submit IS create + send, so nothing may be created or
                 emailed until the person lands here and clicks the explicit
                 "Create and send BAA" control below. Next/Back/refresh/edit never
                 reach this step's submit, and onFormSubmit() hard-blocks any
                 other submit path (e.g. Enter in a text field). -->
            <div v-else-if="phase === 'summary'">
                <div class="bgw-qt">Review and send</div>
                <div class="bgw-qh">Check the details below. Nothing is created or emailed until you choose Send for signature.</div>
                <div class="bgw-summary">
                    <div class="bgw-srow"><span class="bgw-slabel">Business associate</span><span class="bgw-sval">{{ baCompany || '(not set)' }}</span></div>
                    <div class="bgw-srow" v-if="method !== 'paste'"><span class="bgw-slabel">Signer email</span><span class="bgw-sval">{{ signerEmail || '(not set)' }}</span></div>
                    <div class="bgw-srow"><span class="bgw-slabel">Signing passcode</span><span class="bgw-sval">{{ noPassword ? ('No passcode' + (noPasswordReason ? ' - ' + noPasswordReason : '')) : (password ? 'Passcode set' : '(not set)') }}</span></div>
                    <div class="bgw-srow"><span class="bgw-slabel">Review frequency</span><span class="bgw-sval">{{ renewFrequencyLabel }}<span v-if="standardMode" class="bgw-inh">from your standard template</span></span></div>
                    <div class="bgw-srow" v-if="standardMode && dateAutoFields.length">
                        <span class="bgw-slabel">Effective date</span>
                        <span class="bgw-sval"><input type="date" class="bgw-input bgw-date" v-model="effectiveDate" @change="applyEffectiveDate" @input="applyEffectiveDate"> <span class="bgw-inh">defaults to today — change it here if needed</span></span>
                    </div>
                    <div class="bgw-srow" v-if="title"><span class="bgw-slabel">Title</span><span class="bgw-sval">{{ title }}</span></div>
                    <template v-if="type === 'medcurity'">
                        <div class="bgw-ssep">Agreement choices</div>
                        <div class="bgw-srow" v-for="c in answeredChoices" :key="c.slug"><span class="bgw-slabel">{{ c.label }}</span><span class="bgw-sval">{{ c.display }}</span></div>
                        <div class="bgw-srow" v-if="answeredChoices.length === 0"><span class="bgw-sval" style="color:#6b7280;">No optional clauses were customized.</span></div>
                    </template>
                </div>
                <div class="bgw-sendline">
                    <span class="bgw-sendlead">This sends the agreement now.</span>
                    <span class="bgw-sendto">{{ baCompany || 'This business associate' }} will be emailed a secure signing link at <b>{{ signerEmail || '(no signer email set)' }}</b>.</span>
                    <span class="bgw-sendto" v-if="title">Agreement: <b>{{ title }}</b></span>
                    <span class="bgw-sendto">Nothing has been created or emailed yet. Read the full document in the preview beside this before you send.</span>
                </div>
                <div class="bgw-summary-note">The full agreement is in the live preview beside this. Choosing Send for signature creates the subcontractor record and emails the signer a secure link.
                    <template v-if="type === 'medcurity'">
                        <a href="#" class="bgw-editlink" @click.prevent="openAdvanced">Edit this BAA</a>
                        <span v-if="bodyOverrideDirty" class="bgw-inh" style="display:inline;">— custom edits applied to this BAA only</span>
                    </template>
                </div>
            </div>

            <!-- Round 14 (Joe req 2026-09-01): the send is its own control, on the
                 Review step, and it can never be the same DOM element as Next.
                 Both buttons are <button class="bgw-btn primary"> siblings under
                 v-if/v-else, so without distinct keys Vue 2 PATCHES one into the
                 other rather than replacing it: clicking Next on the last
                 pre-Review step ran next(), which set phase='summary'
                 synchronously, and Vue's re-render (a microtask) flipped that
                 very element to type="submit" before the browser performed the
                 click's default action - so the browser submitted the form the
                 user had only meant to advance. The BAA was created and emailed
                 and Review was never shown. Keys force two separate elements;
                 sendArmed below is the belt-and-braces guard. -->
            <div class="bgw-nav">
                <button type="button" class="mc-back-link" v-show="stepIndex > 0" @click="back"><svg class="mc-ob-skip-arrow" viewBox="0 0 40 12" aria-hidden="true"><path d="M5 6 L12 1 M5 6 L12 11 M8 6 H39" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>Back</button>
                <span style="flex:1"></span>
                <button key="bgw-next" type="button" class="bgw-btn primary" v-if="!isLast" :disabled="!canAdvance" @click="next">Next &rarr;</button>
                <button key="bgw-send" type="submit" class="bgw-btn send" v-else :disabled="placeholdersLeft > 0" @click="armSend">Send for signature &rarr;</button>
            </div>
            <div class="bgw-warn" v-if="isLast && placeholdersLeft > 0">{{ placeholdersLeft }} fill-in field(s) still blank. Go back and complete them so nothing is left unfilled.</div>
        </div>

        <!-- ADVANCED "Edit this BAA" — side-by-side, vendor-copy only -->
        <div class="bgw-card bgw-advpanel" v-if="advancedOpen">
            <div class="bgw-advhead">
                <div>
                    <div class="bgw-qt" style="margin-bottom:2px;">Edit this BAA</div>
                    <div class="bgw-qh" style="margin-bottom:0;">Changes here apply <b>only to this vendor's BAA</b>. Your reusable template is not changed.</div>
                </div>
                <button type="button" class="bgw-btn primary" @click="closeAdvanced">Done editing</button>
            </div>
            <div v-if="!bodyOverrideDirty">
                <div v-for="g in advGroups" :key="g.key" class="bgw-advgroup">
                    <div class="bgw-advgt">{{ g.title }}</div>
                    <div v-for="f in g.fields" :key="f.slug" class="bgw-advfield">
                        <div class="bgw-advlabel">{{ f.label }}</div>
                        <div v-if="f.checkbox" class="bgw-options" role="group" :aria-label="f.label">
                            <label v-for="o in f.options" :key="o.value" class="bgw-check-opt" :class="{sel: f.value.indexOf(o.value) !== -1}">
                                <input type="checkbox" class="bgw-radio-input" :value="o.value" v-model="f.value" @change="advChanged(f)">
                                <span class="bgw-check-mark" aria-hidden="true">&#10003;</span>
                                <span class="bgw-radio-text">{{ o.label }}</span>
                            </label>
                        </div>
                        <div v-else-if="f.options && f.options.length" class="bgw-options" role="radiogroup" :aria-label="f.label">
                            <label v-for="o in f.options" :key="o.value" class="bgw-radio-opt" :class="{sel: f.value === o.value}">
                                <input type="radio" class="bgw-radio-input" :name="'bgw-adv-' + f.slug" :value="o.value" v-model="f.value" @change="advChanged(f)">
                                <span class="bgw-radio-mark" aria-hidden="true"></span>
                                <span class="bgw-radio-text">{{ o.label }}</span>
                            </label>
                        </div>
                        <textarea v-else-if="f.multiline" class="bgw-input bgw-area" v-model="f.value" @input="advChanged(f)"></textarea>
                        <input v-else class="bgw-input" type="text" v-model="f.value" @input="advChanged(f)">
                    </div>
                </div>
                <div class="bgw-advfull">
                    <a href="#" class="bgw-editlink" @click.prevent="startBodyOverride">Edit the full text instead</a>
                    <span class="bgw-qh" style="display:block;margin:4px 0 0;">Opens the finished document for free editing. Fill-in questions stop applying once you edit the text directly.</span>
                    <span v-if="previewFetchError" class="bgw-warn" style="display:block;">{{ previewFetchError }}</span>
                </div>
            </div>
            <div v-else class="bgw-advfull">
                <div class="bgw-qh" style="margin-bottom:8px;">You are editing the full text of this BAA (right). The question controls are paused so they can't overwrite your wording.</div>
                <a href="#" class="bgw-editlink" @click.prevent="discardBodyOverride">Discard full-text edits and go back to the questions</a>
            </div>
        </div>
        </div>

        <!-- RIGHT PANE: the live document, sticky beside the questions -->
        <div class="bgw-pane bgw-pane-preview">
        <div class="bgw-card bgw-prevcard">
            <div class="bgw-prevhead"><span class="bgw-pt">&#128196; BAA preview</span><span class="bgw-live" v-if="!bodyOverrideDirty">Live</span><span class="bgw-live" v-else style="color:#b45309;background:#fef3c7;border-color:#fde68a;">Editing text</span></div>
            <div class="bgw-doc" ref="doc">
                <div v-show="bodyOverrideDirty" contenteditable="true" class="bgw-override" ref="overrideDoc" @input="onOverrideInput"></div>
                <div v-show="!bodyOverrideDirty && type === 'medcurity' && templateId" ref="tmpl" v-html="withFields"></div>
                <div v-show="!bodyOverrideDirty && type === 'custom'">
                    <h2 style="text-align:center;color:#0b2545;">{{ title || '[BAA title]' }}</h2>
                    <p v-if="customBody" style="white-space:pre-wrap;">{{ customBody }}</p>
                    <p v-else style="color:#9aa7b8;font-style:italic;">Your agreement text appears here as you write it.</p>
                </div>
                <div v-show="!bodyOverrideDirty && (!type || (type === 'medcurity' && !templateId))" style="color:#9aa7b8;font-style:italic;padding:24px 0;text-align:center;">Pick how you want to create the BAA and the preview will start here.</div>
            </div>
        </div>
        </div>
        </div>
    </form>
</template>

<script>
import { mapState } from 'vuex'
// GR-004 P0-3 — tolerant restore of saved multi-select answers. Kept in a
// plain module so the matching rules are unit-testable without a browser.
import { matchOptionValue, checkboxValuesFromStored } from '../baa/choiceMatching'
// Shared field metadata — FIELD_COPY / DEPENDENT_FIELDS moved verbatim to
// resources/assets/js/baa/templateThemes.js so this wizard and the standard-
// template builder (BaaTemplateBuilder.vue) can never drift apart. The theme
// grouping + vendor-specific slug rules power the advanced "Edit this BAA"
// panel and the streamlined standard send.
import { FIELD_COPY, DEPENDENT_FIELDS, groupFieldsByTheme, isBaNameSlug, isDateAutoSlug, applyPreviewTriggers, autoSizePreviewFields, mountInlineEditors, syncInlineEditors, bindPreviewEditing, applyPreviewReadOnly, mirrorFieldValue } from '../baa/templateThemes'

// FIELD_COPY and DEPENDENT_FIELDS now live in ../baa/templateThemes (see
// the import above) — the maps are unchanged, only relocated for sharing.

export default {
    name: 'BaaGuidedWizard',
    props: ['csrf', 'suggestCe', 'suggestBa', 'medcurityBaasData', 'routesData', 'oldValues', 'defaultTemplateId'],
    data(){
        return {
            method: '',
            type: '',
            templateId: '',
            title: '',
            baCompany: '',
            signerEmail: '',
            noPassword: false,
            password: '',
            noPasswordReason: '',
            reviewFreq: '12',
            customBody: '',
            withFields: '',
            fields: [],
            fieldPos: 0,
            stepIndex: 0,
            phase: 'method',
            // Round 7 (Joe req 2026-09-01): the option list moved to a computed
            // (see `methods` below) so a company with a configured default
            // template is offered that template as the first choice.
            // standardDraft holds the draft/placeholder row the standard send
            // was primed from, so the admin can look at another starting point
            // and come back without losing the vendor's known details.
            standardDraft: null,
            placeholdersLeft: 0,
            templateAutoFailed: false,
            // Item A/D (Joe, 2026-08-14, guided-wizard resume-state integrity):
            // server-computed flag (CompanyBaaController::getOldValues) saying the
            // persisted `baa` body was blank when this draft was loaded. Folded
            // into stepDataValid() so resume can never trust a step index over an
            // agreement body that is actually empty.
            serverBodyEmpty: false,
            // ── Standard send + advanced edit (grok-baa-template-builder) ──
            // standardMode: the streamlined vendor→signer→review path used
            // when the company has a configured default template. Inherited
            // (template-level) and auto-filled (vendor name / dates) fields
            // are tracked by slug so only genuinely missing details are asked.
            standardMode: false,
            hydrated: false,
            inheritedSlugs: {},
            autoSlugs: {},
            effectiveDate: (function(){ var d = new Date(); var m = ('0'+(d.getMonth()+1)).slice(-2); var day = ('0'+d.getDate()).slice(-2); return d.getFullYear()+'-'+m+'-'+day })(),
            // Double-submit token: store() treats a repeat POST of the same
            // uuid as a duplicate and never creates or emails twice. A failed
            // save redirects back and remounts (fresh uuid), so retries work.
            submissionUuid: 'sub-'+Date.now().toString(36)+'-'+Math.random().toString(36).slice(2,10),
            // Advanced "Edit this BAA": side-by-side edit of THIS vendor copy
            // only. bodyOverride (when dirty) is posted as body_override and
            // becomes the vendor BAA's body server-side; the reusable template
            // is never touched and there is deliberately no save-back-to-
            // template control anywhere in this flow.
            advancedOpen: false,
            bodyOverride: '',
            bodyOverrideDirty: false,
            previewFetchError: '',
            // Round 14: a submit is only honored when the person pressed the
            // Review step's own Send control. Cleared on every navigation
            // (setPhaseForStep), so no re-render, Enter key or future refactor
            // can hand the form a submit the user did not ask for.
            sendArmed: false,
        }
    },
    computed: {
        ...mapState('baaForm', ['medcurityBaas','routes','renewFrequencies','oldValues']),
        templates(){ return this.medcurityBaas || [] },
        // The standard/global template is flagged by company_id IS NULL - the existing,
        // documented marker (see 2026_06_25_190000_add_company_owned_baa_templates.php:
        // "NULL = global Medcurity template"). Auto-select ONLY when exactly one such
        // template exists; zero or more than one is ambiguous and must never be guessed
        // by name or list order. selectMethod() falls back to the picker (globalTemplates)
        // when standardTemplate is null.
        globalTemplates(){
            var list = this.medcurityBaas || []
            return list.filter(function(x){ return x.company_id === null || x.company_id === undefined })
        },
        standardTemplate(){
            var globals = this.globalTemplates
            return globals.length === 1 ? globals[0] : null
        },
        // Company-owned templates only. No fallback to the full list - an empty result
        // is a real state; the template-phase UI renders an explicit empty state for it
        // instead of silently substituting unrelated (global) templates.
        savedTemplates(){
            var list = this.medcurityBaas || []
            return list.filter(function(x){ return x.company_id !== null && x.company_id !== undefined })
        },
        // Round 7 (Joe req 2026-09-01): the starting point is ALWAYS asked
        // first. A company with a configured default template sees that
        // template as the first (pre-selected) option, plus "a different
        // template" whenever any other template exists. A company without a
        // default keeps the original four-option Method screen unchanged.
        methods(){
            var base = [
                {key:'medcurity', type:'medcurity', mt:"Medcurity's standard template", md:"Start from our vetted BAA. Answer a few fill-in questions."},
                {key:'saved', type:'medcurity', mt:"One of my saved templates", md:"Use your own approved language. Same guided fill-in and signing."},
                {key:'blank', type:'custom', mt:"Write my own", md:"Start blank and write the agreement yourself."},
                {key:'paste', type:'custom', mt:"Paste an existing BAA", md:"Bring in a BAA you already have. No fill-in step."},
            ]
            var def = this.defaultTemplate
            if(!def) return base
            var out = [{
                key:'standard', type:'medcurity',
                mt: def.name ? ('Use ' + def.name) : 'Use my standard BAA template',
                md: 'Your default template, with your company-wide answers already applied. We only ask for the vendor and the signer.',
            }]
            if(this.otherTemplates.length){
                out.push({key:'other', type:'medcurity', mt:'Start from a different template', md:"Pick another template you have saved, or Medcurity's vetted standard."})
            }
            out.push(base[2])
            out.push(base[3])
            return out
        },
        // Every selectable template that is NOT the company's default.
        otherTemplates(){
            var id = parseInt(this.defaultTemplateId, 10) || 0
            return (this.medcurityBaas || []).filter(function(t){ return t.id !== id })
        },
        // What the 'template' picker step offers, per chosen starting point.
        templateChoices(){
            if(this.method === 'other') return this.otherTemplates
            return this.method === 'saved' ? this.savedTemplates : this.globalTemplates
        },
        // The company's configured default template (standard send fast path).
        // Archived templates never reach the client (filtered server-side).
        defaultTemplate(){
            var id = parseInt(this.defaultTemplateId, 10)
            if(!id) return null
            return (this.medcurityBaas || []).find(function(b){ return b.id === id }) || null
        },
        // Standard mode: the questions actually asked per send — every visible
        // field that is neither inherited from the template's saved answers
        // nor auto-filled (vendor name, agreement dates).
        askedFields(){
            var self = this
            return this.visibleFields.filter(function(f){
                return !self.inheritedSlugs[f.slug] && !self.autoSlugs[f.slug]
            })
        },
        // The list the 'field' phase pages through (standard mode asks only
        // the missing details; the classic flow asks every visible question).
        pagedFields(){
            return this.standardMode ? this.askedFields : this.visibleFields
        },
        dateAutoFields(){
            return this.fields.filter(function(f){ return isDateAutoSlug(f.slug) })
        },
        // Advanced "Edit this BAA" panel: every field, grouped by theme, all
        // editable (vendor-specific ones included — this edits the copy).
        advGroups(){
            var groups = groupFieldsByTheme(this.fields)
            // vendor-specific fields are excluded by the theme grouper; put
            // them up front here, because in the advanced editor they ARE
            // editable for this one vendor copy.
            var self = this
            var vendor = this.fields.filter(function(f){ return isBaNameSlug(f.slug) || isDateAutoSlug(f.slug) || f.slug === 'termination_date' })
            var out = []
            if(vendor.length) out.push({ key: 'vendor', title: 'Parties & dates', fields: vendor })
            groups.forEach(function(g){
                out.push({ key: g.key, title: g.title, fields: g.fields.filter(function(f){ return vendor.indexOf(f) === -1 }) })
            })
            return out.filter(function(g){ return g.fields.length })
        },
        // compact, fixed high-level progress - never one-node-per-field.
        // Item A (Joe, 2026-08-14, guided-wizard resume-state integrity): `done`
        // is derived from stepDataValid() - the SAME check the resume recompute
        // (jumpToFurthestIncompleteStep) uses - never from mere step position, so
        // a dot can never read "done" while its underlying data is actually
        // missing (e.g. an agreement body that is still blank).
        progress(){
            var s = this.stepSequence()
            var seq = s.seq, labels = s.labels
            if(seq.length === 1){
                return [{label: labels[0], done:false, now:true}]
            }
            var ci = seq.indexOf(this.phase); if(ci < 0) ci = 0
            var self = this
            return labels.map(function(l,i){
                var done = i < ci && self.stepDataValid(seq[i])
                return {label:l, done: done, now: i === ci}
            })
        },
        countLabel(){
            if(this.phase === 'method') return 'Start here'
            if(this.phase === 'template') return 'Choose a template'
            // Item 4: "Fill-in N of M" counts only currently-VISIBLE questions -
            // a field hidden by a controlling select's answer is neither asked
            // nor counted.
            if(this.phase === 'field') return 'Fill-in ' + (this.fieldPos + 1) + ' of ' + this.pagedFields.length
            var map = {ba:'Business associate', signer:'Signer', signerpass:'Signer', pass:'Passcode', renew:'Review frequency', summary:'Review', title:'Title', body:'Write the agreement', source:'Bring it in'}
            return map[this.phase] || ''
        },
        currentField(){ return this.pagedFields[this.fieldPos] || {label:'',token:'',help:'',value:'',suggest:'',checkbox:false,options:null} },
        // JW3 P0-B: label for the chosen review frequency, shown on the summary.
        renewFrequencyLabel(){
            var f = this.renewFrequencies || {}
            return f[this.reviewFreq] || (this.reviewFreq ? (this.reviewFreq + ' months') : '(not set)')
        },
        // JW3 P0-B: the customized agreement choices to show on the Review step -
        // every currently-visible fill-in that actually has an answer, rendered
        // as "question -> answer" (checkbox answers joined as a readable list).
        // Purely a read-only summary; it never changes stored values.
        answeredChoices(){
            var out = []
            this.visibleFields.forEach(function(f){
                var display = ''
                if(f.checkbox){
                    if(!Array.isArray(f.value) || f.value.length === 0) return
                    display = f.value.join('; ')
                } else {
                    if(!(f.value && String(f.value).trim())) return
                    display = String(f.value)
                }
                out.push({ slug: f.slug, label: f.label, display: display })
            })
            return out
        },
        // Item 4: fields the person actually gets asked about right now - every
        // extracted field, minus any whose DEPENDENT_FIELDS entry says its
        // controller isn't currently answered with the value that reveals it.
        // fieldPos, "Fill-in N of M", and all step/resume math key off this
        // list (never the raw this.fields), so hidden questions are skipped
        // and re-derived live as controlling answers change.
        visibleFields(){
            var self = this
            return this.fields.filter(function(f){ return self.isFieldVisible(f) })
        },
        formAction(){ return (this.routesData && this.routesData.store) ? this.routesData.store : '' },
        baaName(){ return this.type === 'medcurity' ? (this.baCompany || this.title) : this.title },
        titleSuggest(){ return this.suggestBa ? ('BAA for ' + this.suggestBa) : '' },
        isLast(){ return this.phase === 'summary' },
        canAdvance(){
            if(this.phase === 'method') return !!this.method
            if(this.phase === 'template') return !!this.templateId
            // JW3 P0-A: a checkbox ("Select all that apply") fill-in is optional -
            // selecting none is a valid answer, so it never blocks Next.
            if(this.phase === 'field') return this.currentField.checkbox ? true : !!(this.currentField.value && String(this.currentField.value).trim())
            if(this.phase === 'title') return !!this.title.trim()
            if(this.phase === 'ba') return !!this.baCompany.trim()
            if(this.phase === 'signer') return this.method === 'paste' ? true : !!this.signerEmail.trim()
            if(this.phase === 'signerpass') return !!this.signerEmail.trim() && (this.noPassword ? true : !!(this.password && this.password.trim()))
            if(this.phase === 'source' || this.phase === 'body') return true
            if(this.phase === 'pass') return this.noPassword ? true : !!(this.password && this.password.trim())
            if(this.phase === 'renew') return true
            if(this.phase === 'summary') return true
            return true
        },
    },
    mounted(){
        this.$store.commit('baaForm/setMedcurityBaas', this.medcurityBaasData || []);
        if(this.routesData) this.$store.commit('baaForm/setRoutes', this.routesData);
        this.hydrateFromDraft();
        // Fresh create page (no draft consumed) with a configured default
        // template → the streamlined standard send. Companies without a
        // default keep the classic Method screen exactly as before.
        if(!this.hydrated && this.defaultTemplate){
            // Pass old() values through so a failed-validation round trip
            // re-fills the vendor/signer answers instead of wiping them.
            this.enterStandardMode(this.$props.oldValues || null)
        }
    },
    methods: {
        // P0-2 "Finish BAA" resume. `oldValues` is read via $props (not the
        // bare `this.oldValues`) because this component also declares a
        // same-named VUEX-mapped computed (mapState('baaForm', [...,
        // 'oldValues'])) purely for parity with baa-form-wizard - Vue resolves
        // the name collision in the prop's favour (initState installs the prop
        // as an own instance property before computed properties are set up,
        // and initComputed skips redefining a key already present on the
        // instance), but reading through $props sidesteps that ambiguity
        // entirely and is what we actually want here regardless.
        //
        // Only ever runs from mounted()/methods - no `watch:` block (that
        // silently breaks the #vueApp mount for this component).
        hydrateFromDraft(){
            var d = this.$props.oldValues
            // `id` only appears when getOldValues() loaded a REAL CompanyBaa
            // row (CompanyBaaController::edit -> "Finish BAA"). A fresh Create
            // page's old_values (validation-failure old() redisplay, or the
            // company's default-template hint) never carries one, so a brand
            // new BAA still starts on the Method screen as designed.
            if(!d || typeof d !== 'object' || !d.id) return
            this.hydrated = true

            // Item D: the server's own read of whether the persisted `baa` body is
            // blank (CompanyBaaController::getOldValues). stepDataValid() folds
            // this in so a resumed draft can never be walked past a step whose
            // agreement body is actually empty, regardless of what the row's own
            // step/phase might otherwise imply.
            this.serverBodyEmpty = !!d.bodyEmpty

            // A vendor placeholder / legacy row that has NEVER started a BAA
            // (Dev neverStarted flag: custom-typed, empty body, no template,
            // no file, no agreement title). Do NOT resume as a custom draft.
            // If the company has a standard template, enter the streamlined
            // send pre-filled with the vendor's known details. Otherwise
            // start on the Method screen as if creating from scratch, with
            // contact-level context still prefilled (BA company, signer,
            // password, review cadence). Genuine in-progress drafts always
            // fail neverStarted and resume classically below.
            if(d.neverStarted){
                if(this.defaultTemplate){
                    this.enterStandardMode(d)
                } else {
                    this.hydrateContactContext(d)
                }
                return
            }

            if(d.type === 'medcurity'){
                this.type = 'medcurity'
                if(d.template){
                    this.templateId = String(d.template)
                    var tmpl = (this.medcurityBaas || []).find(function(b){ return b.id === parseInt(d.template, 10) })
                    var isGlobal = !!tmpl && (tmpl.company_id === null || tmpl.company_id === undefined)
                    this.method = isGlobal ? 'medcurity' : 'saved'
                    this.onTemplateChosen()
                }
            } else if(d.type === 'custom'){
                this.type = 'custom'
                this.method = 'blank'
                this.title = d.agreement_title || d.name || ''
                this.customBody = d.baa || ''
            } else {
                return
            }

            this.hydrateContactContext(d)

            // extractFields() (triggered by onTemplateChosen above) runs inside
            // its own $nextTick after the with_fields HTML re-renders - wait one
            // more tick so this.fields is populated before we fill it in and
            // compute where to land.
            var self = this
            this.$nextTick(function(){
                self.$nextTick(function(){
                    self.applyDraftFieldValues(d)
                    // Round 8b (Joe req, 2026-09-01): ?intent=passcode (see
                    // CompanyBaaController::edit()) puts `jumpToStep` on
                    // old_values so the Actions-tab "Set signing passcode"
                    // button can land here directly, bypassing
                    // jumpToFurthestIncompleteStep()'s "first incomplete
                    // step" walk -- which stops at an EARLIER gap (e.g. an
                    // empty body on a record that is already sent) before
                    // ever reaching Passcode.
                    if(d.jumpToStep){
                        self.jumpToNamedStep(d.jumpToStep)
                    } else {
                        self.jumpToFurthestIncompleteStep()
                    }
                })
            })
        },
        // JW3 P1-C (Joe, 2026-08-15): reload every saved answer into its control
        // when an existing BAA is edited. Stored input keys are the INDEXED field
        // name (slug[genId], with a '|'-joined list under one key for a checkbox
        // group). The previous version looked up d[f.name] by the EXACT indexed
        // name, so it silently missed whenever the generated-input-id drifted
        // between the render that saved the draft and this one (a re-seeded
        // template, an added saved template, or any field reorder shifts every
        // index) - which is exactly why a saved "No" came back unselected.
        // Matching by the stable SLUG fixes that permanently, preserves explicit
        // No/false/0 answers, and rebuilds checkbox arrays. Done in three passes
        // so all controllers are set before any hidden dependent is cleared.
        // Extracted from hydrateFromDraft (BAA never-started fix): vendor
        // contact-level context that does not complete any wizard step (BA
        // company, signer email, review cadence, passcode). Called both from
        // the never-started early-exit and the normal resume path below it -
        // behavior on the resume path is unchanged, this is a pure extraction.
        hydrateContactContext(d){
            this.baCompany = d.ba_company_name || ''
            if(Array.isArray(d.associates) && d.associates.length && d.associates[0].email){
                this.signerEmail = d.associates[0].email
            }
            if(d.renewal_frequency){ this.reviewFreq = String(d.renewal_frequency) }

            var waived = !!(parseInt(d.allow_no_password, 10) || d.allow_no_password === true)
            this.noPassword = waived
            if(waived){
                this.noPasswordReason = d.no_password_reason || ''
            } else if(d.password){
                this.password = d.password
            }
        },
        applyDraftFieldValues(d){
            var self = this
            d = d || {}
            // slug -> [{key, val}] in stored order (index order for a group).
            var bySlug = {}
            Object.keys(d).forEach(function(k){
                var m = /^([A-Za-z0-9_]+)\[/.exec(k)
                if(!m) return
                var slug = m[1]
                if(!bySlug[slug]) bySlug[slug] = []
                bySlug[slug].push({ key: k, val: d[k] })
            })
            // Pass 1: assign values (no DOM writes, no visibility enforcement).
            this.fields.forEach(function(f){
                var entries = bySlug[f.slug]
                var raw = null
                if(Object.prototype.hasOwnProperty.call(d, f.name)) raw = d[f.name]          // exact indexed hit (fast path when indexes DO line up)
                else if(entries && entries.length) raw = entries[0].val                       // any stored value for this slug
                else if(Object.prototype.hasOwnProperty.call(d, f.slug)) raw = d[f.slug]      // bare-slug fallback
                if(f.checkbox){
                    // GR-004 P0-3: rebuild the selected-values array from
                    // whatever shape the answer was stored in, and canonicalize
                    // every hit onto the option's OWN value string. reflectField()
                    // matches with vals.indexOf(e.value), an exact comparison, so
                    // a restored value that differs from the re-rendered option by
                    // so much as a CRLF or an HTML entity silently leaves the box
                    // unchecked - which is exactly how saved clauses disappeared
                    // on edit and got dropped on the next save.
                    var pool = (entries && entries.length) ? entries.map(function(e){ return e.val }) : (raw === null || raw === undefined ? [] : [raw])
                    f.value = self.checkboxValuesFromStored(f, pool)
                } else {
                    if(raw === null || raw === undefined) return
                    if(String(raw) === '') return   // a genuinely blank scalar: keep the template default
                    f.value = raw
                }
            })
            // Pass 2: reflect every field into the live preview.
            this.fields.forEach(function(f){ self.reflectField(f) })
            // Pass 3: enforce visibility once, now that every controller is set,
            // so a dependent hidden by its (restored) controller is cleared, and
            // one revealed by it keeps its restored answer.
            this.enforceFieldVisibility()
        },
        // ── GR-004 P0-3: tolerant restore of saved multi-select answers ──────
        //
        // A "Select all that apply" clause is stored as the clause TEXT itself
        // (policy-input.blade.php renders value="{{ $option }}"), so restoring a
        // saved answer means string-matching long, punctuated, sometimes
        // multi-line prose back onto a freshly re-rendered option. Several
        // things legitimately perturb that string between save and reload:
        //
        //   - HTTP form encoding normalizes newlines to CRLF, so a multi-line
        //     clause comes back with \r\n where the template has \n;
        //   - the value round-trips through an HTML attribute, so &, ", ' and <
        //     may return as entities;
        //   - indentation/whitespace in the template can be reflowed;
        //   - older saves joined arrays with String(array) -> comma-separated,
        //     before the current '|' join existed.
        //
        // Any one of those made the exact comparison in reflectField() miss, the
        // box render unchecked, and the clause vanish from the document on the
        // next save. Normalizing BOTH sides for the comparison - then storing the
        // option's own canonical value - fixes every one of those cases at once,
        // for existing rows as well as new ones, with no data migration and no
        // destructive rewrite of historical answers.
        // Implemented in resources/assets/js/baa/choiceMatching.js so the
        // matching rules are unit-testable outside a browser
        // (tests/js/choiceMatching.test.mjs) rather than only reachable
        // through a mounted wizard.
        matchOptionValue(f, candidate){
            return matchOptionValue(f.options || [], candidate)
        },
        checkboxValuesFromStored(f, pool){
            return checkboxValuesFromStored(f.options || [], pool)
        },
        // ── Standard streamlined send (grok-baa-template-builder) ─────────
        // Enter the fast path: default template auto-selected, template-level
        // answers inherited, vendor name + dates auto-filled, review cadence
        // taken from the template. `d` is an optional draft/placeholder row to
        // pre-fill vendor/signer details from.
        enterStandardMode(d){
            var t = this.defaultTemplate
            if(!t) return
            // Remember the row this was primed from so re-picking the standard
            // option after looking at another starting point still pre-fills.
            if(d){ this.standardDraft = d } else { d = this.standardDraft }
            this.standardMode = true
            this.hydrated = true
            // The template body is being loaded fresh — the placeholder's own
            // empty body must not poison step validity (see fieldsDataValid).
            this.serverBodyEmpty = false
            this.method = 'standard'
            this.type = 'medcurity'
            this.templateId = String(t.id)
            this.withFields = t.with_fields || t.baa || ''
            if(t.renewal_frequency){ this.reviewFreq = String(t.renewal_frequency) }
            if(d){
                this.baCompany = d.ba_company_name || d.name || ''
                if(Array.isArray(d.associates) && d.associates.length && d.associates[0].email){
                    this.signerEmail = d.associates[0].email
                } else if(Array.isArray(d.associate_email) && d.associate_email.length && d.associate_email[0]){
                    // old() shape from a failed-validation repost
                    this.signerEmail = d.associate_email[0]
                }
            }
            // Round 7 (Joe req 2026-09-01): park on the Start step and ASK which
            // template this BAA begins from. The default is pre-selected, so the
            // streamlined send stays one click away, but it is never entered
            // silently - this used to jump straight to 'ba' and the admin never
            // saw that a template choice existed at all.
            this.stepIndex = 0
            this.phase = 'method'
            var self = this
            this.$nextTick(function(){
                self.extractFields()
                self.$nextTick(function(){
                    self.applyTemplateAnswers(t)
                    self.applyAutoBindings()
                    self.enforceFieldVisibility()
                })
            })
        },
        // Apply the template's saved company-wide answers to the extracted
        // fields. An applied slug is marked inherited and is not re-asked on
        // the normal path (it stays editable in the advanced editor).
        applyTemplateAnswers(t){
            var answers = (t && t.template_answers) || {}
            if(Array.isArray(answers)) answers = {}
            var marked = {}
            var self = this
            this.fields.forEach(function(f){
                if(!Object.prototype.hasOwnProperty.call(answers, f.slug)) return
                var raw = answers[f.slug]
                if(f.checkbox){
                    var pool = Array.isArray(raw) ? raw : [raw]
                    f.value = self.checkboxValuesFromStored(f, pool)
                } else if(f.options && f.options.length){
                    var match = self.matchOptionValue(f, String(raw))
                    f.value = (match === null || match === undefined) ? String(raw) : match
                } else {
                    f.value = String(raw)
                }
                self.reflectField(f)
                marked[f.slug] = true
            })
            this.inheritedSlugs = marked
        },
        // Auto-fill vendor-specific fields: the BA legal name mirrors the
        // Vendor step's answer; agreement dates default to the effective date
        // (today, editable at Review). Marked so they are never asked.
        applyAutoBindings(){
            var marked = {}
            var self = this
            this.fields.forEach(function(f){
                if(isBaNameSlug(f.slug)){
                    marked[f.slug] = 'ba'
                    if(self.baCompany){ f.value = self.baCompany; self.reflectField(f) }
                } else if(isDateAutoSlug(f.slug)){
                    marked[f.slug] = 'date'
                    f.value = self.effectiveDateLong()
                    self.reflectField(f)
                }
            })
            this.autoSlugs = marked
            // autoSlugs is what decides which controls are read-only, and it is
            // only known now, so re-apply the lock against the fresh map.
            applyPreviewReadOnly(this.$refs.doc, this.isPreviewReadOnly)
            this.countPlaceholders()
        },
        // Live re-sync of BA-name-bound fields as the Vendor answer is typed.
        syncAutoBindings(){
            if(!this.standardMode) return
            var self = this
            this.fields.forEach(function(f){
                if(self.autoSlugs[f.slug] === 'ba'){
                    f.value = self.baCompany
                    self.reflectField(f)
                }
            })
            this.countPlaceholders()
        },
        // Review-step effective-date edit → re-fill every date-bound field.
        applyEffectiveDate(){
            var self = this
            this.fields.forEach(function(f){
                if(self.autoSlugs[f.slug] === 'date'){
                    f.value = self.effectiveDateLong()
                    self.reflectField(f)
                }
            })
            this.countPlaceholders()
        },
        // 'YYYY-MM-DD' → 'August 23, 2026' (the format the agreement prints).
        effectiveDateLong(){
            var v = this.effectiveDate
            if(!v) return this.today()
            var parts = String(v).split('-')
            if(parts.length !== 3) return this.today()
            var d = new Date(parseInt(parts[0],10), parseInt(parts[1],10)-1, parseInt(parts[2],10))
            if(isNaN(d.getTime())) return this.today()
            return d.toLocaleDateString('en-US', {year:'numeric', month:'long', day:'numeric'})
        },
        // ── Advanced "Edit this BAA" (vendor-copy only) ───────────────────
        openAdvanced(){
            this.advancedOpen = true
            this.previewFetchError = ''
        },
        closeAdvanced(){
            this.advancedOpen = false
        },
        // A manual edit to an auto-bound field in the advanced panel wins over
        // the binding from then on (the person is deliberately overriding).
        advChanged(f){
            if(this.autoSlugs[f.slug]){
                var next = {}
                var self = this
                Object.keys(this.autoSlugs).forEach(function(k){ if(k !== f.slug) next[k] = self.autoSlugs[k] })
                this.autoSlugs = next
            }
            this.syncField(f)
        },
        // Switch to free-text editing of the FINAL document. The body is
        // compiled server-side (route baa.preview-compiled — strictly
        // read-only) so what is edited is byte-identical to what store()
        // would save; the result posts as body_override and only ever touches
        // this vendor's copy.
        startBodyOverride(){
            var route = this.routesData && this.routesData.preview
            if(!route || !this.$refs.form){ this.previewFetchError = 'Full-text editing is unavailable right now.'; return }
            var self = this
            this.previewFetchError = ''
            var fd = new FormData(this.$refs.form)
            fd.delete('send_on_save')
            window.fetch(route, {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'X-CSRF-TOKEN': this.csrf, 'Accept': 'application/json' },
                body: fd,
            }).then(function(resp){
                if(!resp.ok) throw new Error('preview failed: ' + resp.status)
                return resp.json()
            }).then(function(json){
                self.bodyOverride = String(json.body || '')
                self.bodyOverrideDirty = true
                self.$nextTick(function(){
                    if(self.$refs.overrideDoc){ self.$refs.overrideDoc.innerHTML = self.bodyOverride }
                })
            }).catch(function(){
                self.previewFetchError = 'Could not open the full text for editing. Check your connection and try again.'
            })
        },
        onOverrideInput(e){
            this.bodyOverride = e && e.target ? e.target.innerHTML : this.bodyOverride
        },
        discardBodyOverride(){
            this.bodyOverride = ''
            this.bodyOverrideDirty = false
            if(this.$refs.overrideDoc){ this.$refs.overrideDoc.innerHTML = '' }
        },
        // The high-level step sequence (+ labels) for the currently chosen
        // method/type. Shared by `progress` (dots) and the resume recompute
        // below, so both always agree on what steps exist and in what order.
        stepSequence(){
            // Standard streamlined send: vendor → (missing details) → signer →
            // review. Clause questions answered once at the template level are
            // inherited, never re-asked; dates and the vendor name auto-fill.
            if(this.standardMode){
                var hasAsked = this.askedFields.length > 0
                var seqS = hasAsked ? ['method','ba','field','signerpass','summary'] : ['method','ba','signerpass','summary']
                var labS = hasAsked ? ['Start','Vendor','Details','Signer','Review'] : ['Start','Vendor','Signer','Review']
                return { seq: seqS, labels: labS }
            }
            var method = this.method
            if(!method){
                return { seq: ['method'], labels: ['Method'] }
            }
            var mDef = null
            for(var i = 0; i < this.methods.length; i++){
                if(this.methods[i].key === method){ mDef = this.methods[i]; break }
            }
            var t = mDef ? mDef.type : ''
            var seq, labels
            if(t === 'medcurity'){
                if(method === 'saved' || method === 'other' || this.templateAutoFailed){
                    seq=['method','template','field','ba','signer','pass','renew','summary']
                    labels=['Method','Template','Details','Business associate','Signer','Passcode','Renewal','Review']
                } else {
                    seq=['method','field','ba','signer','pass','renew','summary']
                    labels=['Method','Details','Business associate','Signer','Passcode','Renewal','Review']
                }
            } else if(method === 'blank'){
                seq=['method','title','body','ba','signer','pass','renew','summary']
                labels=['Method','Title','Write it','Business associate','Signer','Passcode','Renewal','Review']
            } else {
                seq=['method','source','title','ba','signer','pass','renew','summary']
                labels=['Method','Bring it in','Title','Business associate','Signer','Passcode','Renewal','Review']
            }
            return { seq: seq, labels: labels }
        },
        // Item A (Joe, 2026-08-14, guided-wizard resume-state integrity): the one
        // place that decides whether a given HIGH-LEVEL step's underlying data
        // actually exists, independent of any saved step/phase index. Used by
        // both the resume recompute (jumpToFurthestIncompleteStep) and the
        // progress dots (`progress` computed), so a dot can never read "done"
        // for a step whose data is missing, and resume can never land past one.
        stepDataValid(name){
            if(name === 'method') return !!this.method
            if(name === 'template') return !!this.templateId
            if(name === 'field') return this.fieldsDataValid()
            if(name === 'title') return !!(this.title && String(this.title).trim())
            if(name === 'body' || name === 'source') return this.bodyDataValid()
            if(name === 'ba') return !!(this.baCompany && String(this.baCompany).trim())
            if(name === 'signer') return this.method === 'paste' ? true : !!(this.signerEmail && String(this.signerEmail).trim())
            if(name === 'pass') return this.noPassword ? true : !!(this.password && String(this.password).trim())
            if(name === 'signerpass') return !!(this.signerEmail && String(this.signerEmail).trim()) && (this.noPassword ? true : !!(this.password && String(this.password).trim()))
            if(name === 'renew') return true
            if(name === 'summary') return true
            return true
        },
        // A template method's agreement body is only real once a template is
        // actually loaded AND every fill-in question it produced has an answer.
        // Zero extracted fields means the template never loaded ("nothing to
        // answer" is NOT the same as "everything answered") - that gap is
        // exactly how a resumed draft could skip straight past "Details" while
        // the live preview still showed a blank body. serverBodyEmpty (the
        // persisted document, per the server) overrides everything else.
        fieldsDataValid(){
            if(this.serverBodyEmpty) return false
            if(!this.templateId || !this.withFields) return false
            if(!this.fields || this.fields.length === 0) return false
            // Item 4: only currently-VISIBLE questions must be answered - a
            // field hidden by its controller's answer is never asked, so its
            // blank value must never block "Details" (or the whole resume)
            // from being considered complete.
            var visible = this.visibleFields
            for(var i = 0; i < visible.length; i++){
                // JW3 P0-A: a checkbox fill-in is optional (none is a valid
                // answer), so a blank one must never block "Details" or resume.
                if(visible[i].checkbox) continue
                var v = visible[i].value
                if(!(v && String(v).trim())) return false
            }
            return true
        },
        // A custom (write-my-own / paste) method's agreement body is only real
        // once there is actual text in it - never trust a saved step index over
        // an empty customBody.
        bodyDataValid(){
            if(this.serverBodyEmpty) return false
            return !!(this.customBody && String(this.customBody).trim())
        },
        // Which fill-in field (index INTO visibleFields) is the first one still
        // unanswered - where the 'field' phase should land when resuming
        // mid-way through Details. Item 4: indexes visibleFields, not the raw
        // fields array, so resume can never land on a question that's
        // currently hidden by its controller's answer.
        firstUnansweredFieldIndex(){
            var visible = this.visibleFields
            for(var i = 0; i < visible.length; i++){
                if(visible[i].checkbox) continue
                var v = visible[i].value
                if(!(v && String(v).trim())) return i
            }
            return 0
        },
        // Inverse of setPhaseForStep()'s index math: given a target phase name
        // (and, for 'field', which field), return the concrete stepIndex that
        // lands there. Kept in exact lockstep with setPhaseForStep() so resume
        // and ordinary Back/Next navigation can never disagree about what step
        // number a given question lives at.
        stepIndexForPhase(name, fieldPos){
            if(this.standardMode){
                var nAsk = this.askedFields.length
                if(name === 'method') return 0
                if(name === 'ba') return 1
                if(name === 'field') return 2 + (fieldPos || 0)
                if(name === 'signerpass') return 2 + nAsk
                return 3 + nAsk // summary
            }
            if(name === 'method') return 0
            if(this.type === 'medcurity'){
                var base = (this.method === 'saved' || this.method === 'other' || this.templateAutoFailed) ? 1 : 0
                if(name === 'template') return 1
                var fStart = 1 + base
                if(name === 'field') return fStart + (fieldPos || 0)
                // Same Math.max floor as setPhaseForStep() - the field slot is
                // never narrower than one step even with zero fields extracted.
                // Item 4: sized off visibleFields, not the raw fields array, so
                // the step count shrinks/grows live as trigger-hidden questions
                // come and go.
                var fEnd = Math.max(fStart, base + this.visibleFields.length)
                var afterMap = {ba:1, signer:2, pass:3, renew:4, summary:5}
                return fEnd + (afterMap[name] || 5)
            }
            var order = (this.method === 'blank')
                ? ['title','body','ba','signer','pass','renew','summary']
                : ['source','title','ba','signer','pass','renew','summary']
            var pos = order.indexOf(name)
            return (pos < 0 ? order.length - 1 : pos) + 1
        },
        // Never trust the row's own saved step/phase. Walk the step sequence
        // from the top and stop at the first step whose required DATA is
        // actually missing (stepDataValid) - landing on Review only once every
        // prior step's data genuinely checks out. This is what fixes the false
        // "steps 1-5 complete" resume: a blank agreement body (no template
        // loaded, or a custom body with no text) now always wins over whatever
        // step index the draft happened to carry.
        jumpToFurthestIncompleteStep(){
            var seq = this.stepSequence().seq
            var landOn = 'summary'
            var landField = 0
            for(var i = 0; i < seq.length; i++){
                var name = seq[i]
                if(name === 'summary') break
                if(!this.stepDataValid(name)){
                    landOn = name
                    if(name === 'field'){ landField = this.firstUnansweredFieldIndex() }
                    break
                }
            }
            this.stepIndex = this.stepIndexForPhase(landOn, landField)
            this.setPhaseForStep()
            this.$nextTick(this.scrollToQuestion)
        },
        // Round 8b (Joe req, 2026-09-01): land directly on a NAMED step,
        // skipping jumpToFurthestIncompleteStep()'s "first incomplete step"
        // walk entirely. Used by the ?intent=passcode deep link (see
        // hydrateFromDraft above) so an earlier, unrelated gap elsewhere in
        // the draft can never intercept the jump. standardMode has no
        // separate Passcode step -- signer and passcode share one card,
        // phase 'signerpass' -- so that combined step is the closest
        // equivalent there; any other name not present in the current
        // sequence falls back to Review rather than an undefined step index.
        jumpToNamedStep(name){
            var seq = this.stepSequence().seq
            var target = (name === 'pass' && this.standardMode) ? 'signerpass' : name
            if(seq.indexOf(target) === -1){ target = 'summary' }
            this.stepIndex = this.stepIndexForPhase(target, 0)
            this.setPhaseForStep()
            this.$nextTick(this.scrollToQuestion)
        },
        // Choosing the method also resolves the template. Medcurity's standard
        // needs no picker: auto-select the vetted global template and extract
        // its fill-in fields now, so Next goes straight to the first question.
        selectMethod(key){
            // Round 7: the standard (default-template) option re-enters the
            // streamlined send; any other option leaves it, dropping the
            // template-inherited and auto-filled answers so the newly chosen
            // starting point begins genuinely clean.
            if(key === 'standard'){
                this.enterStandardMode(this.standardDraft)
                return
            }
            if(this.standardMode){ this.exitStandardMode() }
            this.method = key
            var m = (this.methods.find(function(x){ return x.key === key }) || {})
            this.type = m.type || ''
            this.templateAutoFailed = false
            if(key === 'medcurity'){
                var std = this.standardTemplate
                // Exactly one global template required to auto-select (see standardTemplate
                // computed). Ambiguous (0 or 2+) -> fail loud, fall back to the picker so
                // the person can still proceed instead of silently guessing wrong.
                if(std){
                    this.templateId = String(std.id); this.onTemplateChosen()
                } else {
                    this.templateAutoFailed = true
                    this.templateId = ''
                    this.withFields = ''
                    this.fields = []
                }
            } else {
                this.templateId = ''
                this.withFields = ''
                this.fields = []
            }
        },
        // Round 7: leave the streamlined standard send. Vendor and signer
        // answers are facts about the relationship, not template answers, so
        // they survive; everything inherited from the template does not.
        exitStandardMode(){
            this.standardMode = false
            this.inheritedSlugs = {}
            this.autoSlugs = {}
            this.templateId = ''
            this.withFields = ''
            this.fields = []
            this.stepIndex = 0
            this.phase = 'method'
        },
        onTemplateChosen(){
            var t = (this.medcurityBaas || []).find(b => b.id === parseInt(this.templateId))
            this.withFields = t ? (t.with_fields || t.baa || '') : ''
            this.$nextTick(this.extractFields)
        },
        // Suggest a known value for a merge field by its slug. Tight matches only, so we
        // never drop the vendor name into an unrelated "ba_can_use_phi" style field.
        suggestFor(slug){
            var s = (slug || '').toLowerCase()
            if(/(^|_)(as_of_date|effective_date|agreement_effective_date|date)(_|$)|_date$|^date$/.test(s)) return this.today()
            if(/(covered_entity_name|^ce_name$|entity_legal_name)/.test(s)) return this.suggestCe || ''
            if(/(business_associate_name|^ba_name$|^vendor_name$|associate_legal_name)/.test(s)) return this.suggestBa || ''
            return ''
        },
        // Joe (2026-08-14, dedupe): a template can use the same %%token%% more
        // than once (e.g. %%business_associate%% appearing in both the intro
        // paragraph and a later clause) - the DOM then has one input/textarea/
        // select per OCCURRENCE, not per fact. Group elements by slug first so
        // the wizard asks each merge fact exactly once; `els` holds every DOM
        // element that token maps to, and syncField()/the pre-fill reflection
        // below write the one answer into all of them. Fill-in "N of M" is
        // therefore counted in unique slugs, matching what the person actually
        // has to answer.
        extractFields(){
            var root = this.$refs.tmpl
            this.fields = []
            if(!root) return
            var domEls = root.querySelectorAll('input, textarea, select')
            var self = this
            var bySlug = {}
            var order = []
            Array.prototype.forEach.call(domEls, function(el){
                var name = el.getAttribute('name') || ''
                if(!name) return
                var slug = name.replace(/\[.*$/,'')
                if(!bySlug[slug]){ bySlug[slug] = []; order.push(slug) }
                bySlug[slug].push(el)
            })
            order.forEach(function(slug){
                var elsForSlug = bySlug[slug]
                var el = elsForSlug[0]   // first occurrence sets label/type/options - duplicates must be the same field
                var name = el.getAttribute('name') || ''
                // Item 2: FIELD_COPY's hand-written plain-English question wins
                // when we have one for this slug; otherwise fall back exactly as
                // before (template's own placeholder, then humanize(slug)) so a
                // custom/saved template's own fields still get a readable title.
                var copy = FIELD_COPY[slug] || null
                var label = (copy && copy.title) || el.getAttribute('placeholder') || self.humanize(slug)
                var help = (copy && copy.help) || ''
                var known = self.suggestFor(slug)
                var deflt = el.value || ''
                // JW3 P0-A (Joe, 2026-08-15): a template checkbox field renders
                // server-side as a GROUP of <input type=checkbox name="slug[id][]">,
                // one per option. Detect the whole group and build a real
                // multi-select: options come from every checkbox's value (the
                // full clause text), the answer is the array of currently-checked
                // values, and syncField() drives el.checked on each underlying
                // preview input so the form still posts slug[id][]=<clause> for
                // each selection - exactly what generateMedcurityContent() reads.
                var isCheckbox = (el.tagName === 'INPUT' && (el.type === 'checkbox' || (el.getAttribute('type') || '').toLowerCase() === 'checkbox'))
                if(isCheckbox){
                    var cbOptions = []
                    var checkedVals = []
                    elsForSlug.forEach(function(e){
                        var v = e.value
                        if(v === null || v === undefined) return
                        if(!cbOptions.some(function(o){ return o.value === v })){
                            // Prefer the associated <label> text, fall back to the
                            // value (which already carries the full clause).
                            var lbl = v
                            var id = e.getAttribute('id')
                            if(id){ var lel = root.querySelector('label[for="' + (window.CSS && CSS.escape ? CSS.escape(id) : id) + '"]'); if(lel && lel.textContent.trim()) lbl = lel.textContent.trim() }
                            cbOptions.push({ value: v, label: lbl })
                        }
                        if(e.checked && checkedVals.indexOf(v) === -1) checkedVals.push(v)
                    })
                    self.fields.push({
                        els: elsForSlug, name: name, slug: slug, token: '%%'+slug+'%%', label: label, help: help,
                        options: cbOptions, checkbox: true, multiline: false,
                        value: checkedVals, suggest: ''
                    })
                    self.reflectField(self.fields[self.fields.length - 1])
                    return
                }
                // Joe (2026-08-14): a SELECT in the template carries its own
                // vocabulary - capture the options so the question card renders
                // the SAME dropdown instead of a free-text box whose answer
                // could never match (and was silently lost on save).
                var options = null
                if(el.tagName === 'SELECT'){
                    options = []
                    Array.prototype.forEach.call(el.options, function(o){
                        // Skip empty-value entries: those are the inline
                        // select's own placeholder ("Stricter timeframe?") and
                        // would duplicate the question card's "Choose an
                        // option" placeholder.
                        if(o.value !== ''){
                            options.push({ value: o.value, label: (o.textContent || o.value).trim() })
                        }
                    })
                    // A select pre-fills only from its own current selection,
                    // never from name-based suggestions (those are free text).
                    known = ''
                }
                var value = (known || deflt)  // pre-fill from a known value or the template default
                self.fields.push({
                    els: elsForSlug, name: name, slug: slug, token: '%%'+slug+'%%', label: label, help: help,
                    options: options, checkbox: false,
                    multiline: el.tagName === 'TEXTAREA',
                    value: value,
                    suggest: options ? '' : value  // accept-able suggestion (free-text fields only)
                })
                if(!options && value){
                    // reflect the pre-fill into EVERY occurrence in the live preview immediately
                    elsForSlug.forEach(function(e){ e.value = value })
                }
            })
            // Item 4: a field hidden by its controller's answer must never carry
            // a stray pre-fill (template default, name-based suggestion) into
            // either its question or the live preview - clear it immediately,
            // before the person ever sees it.
            this.enforceFieldVisibility()
            // Round 10 (Joe req 2026-09-01): the same preview blindness Joe hit
            // in the template builder exists here - a conditional clause is
            // server-rendered display:none and is only revealed by the option's
            // data-triggers contract, which nothing re-runs when a value is set
            // programmatically. Apply it against the rendered document so the
            // side-by-side preview shows exactly what will be signed.
            applyPreviewTriggers(this.$refs.doc)
            // Round 11: size every revealed fill-in to its content (see
            // BaaTemplateBuilder). After the reveal, never before.
            autoSizePreviewFields(this.$refs.doc)
            // Round 12: inline editable shims so continuation clauses wrap.
            mountInlineEditors(this.$refs.doc, { onInput: this.onInlineEdit })
            // Round 13: bind every other preview control too (see
            // BaaTemplateBuilder). Here the preview controls already post with
            // the form, but the component's field state did not know they had
            // changed, so a later reflect could silently revert them.
            // Round 13b: lock what has no editable home here, before binding.
            applyPreviewReadOnly(this.$refs.doc, this.isPreviewReadOnly)
            bindPreviewEditing(this.$refs.doc, {
                onInput: this.onInlineEdit,
                getField: this.fieldBySlug,
            })
        },
        // Round 13b: in the streamlined standard send the agreement dates are
        // DERIVED from the Effective date control on the Review step
        // (applyAutoBindings writes effectiveDateLong() into them), so the
        // document is not their home and a typed long-form date could not be
        // mapped back to that control's ISO value without guessing. They are
        // read-only here and edited at Review. The vendor legal name IS
        // two-way: its home is the Vendor step's own answer (baCompany), which
        // onInlineEdit writes to below. Every other field's home is f.value.
        isPreviewReadOnly(slug){
            return this.autoSlugs[slug] === 'date'
        },
        fieldBySlug(slug){
            return this.fields.find(function(f){ return f.slug === slug })
        },
        // Round 12: an edit made in the document's inline shim, routed through
        // the same field state a question-pane edit uses.
        onInlineEdit(slug, value, sourceEl){
            // Round 13b: the vendor legal name is DERIVED from the Vendor step's
            // answer, so writing only the merge field wrote to a copy that
            // syncAutoBindings() overwrote on the next keystroke. Write the home
            // first; the field below then agrees with it.
            if(isBaNameSlug(slug)){ this.baCompany = value }
            var field = this.fieldBySlug(slug)
            if(!field) return
            field.value = value
            // Round 13: mirror into the field's OTHER elements only, so the
            // control being typed in keeps its caret.
            mirrorFieldValue(field, value, sourceEl)
            this.enforceFieldVisibility()
            applyPreviewTriggers(this.$refs.doc)
            autoSizePreviewFields(this.$refs.doc)
            syncInlineEditors(this.$refs.doc)
            this.countPlaceholders()
        },
        humanize(s){ return (s||'').replace(/_/g,' ').replace(/\b\w/g, function(c){return c.toUpperCase()}) },
        today(){ var d = new Date(); return d.toLocaleDateString('en-US', {year:'numeric', month:'long', day:'numeric'}) },
        // Item 4: verified in database/seeders/BAASeeder.php - see
        // DEPENDENT_FIELDS above for exactly which relationships this covers
        // and why. A field with no entry (every field on a custom/saved
        // template, and every field on the standard template that isn't one of
        // the six verified dependents) is always visible.
        isFieldVisible(f){
            var dep = DEPENDENT_FIELDS[f.slug]
            if(!dep) return true
            var controller = this.fields.find(function(x){ return x.slug === dep.controller })
            return !!controller && controller.value === dep.showWhen
        },
        // Item 4: run after every value change (syncField) and after
        // extraction/resume. Clears - value AND every DOM element in the live
        // preview - any field that is not currently visible, so switching a
        // controlling answer No -> Yes -> No repeatedly can never resurrect a
        // stale answer, and a hidden field's blank value can never block Next/
        // Review & send (countPlaceholders only counts visible fields).
        enforceFieldVisibility(){
            var self = this
            this.fields.forEach(function(f){
                var hasValue = f.checkbox ? (Array.isArray(f.value) && f.value.length > 0) : !!f.value
                if(!self.isFieldVisible(f) && hasValue){
                    if(f.checkbox){
                        f.value = []
                        if(f.els){ f.els.forEach(function(e){ e.checked = false }) }
                    } else {
                        f.value = ''
                        if(f.els){ f.els.forEach(function(e){ e.value = '' }) }
                    }
                }
            })
            this.countPlaceholders()
            // If the question currently on screen just became hidden (its own
            // controller was changed while it was on screen via Back), clamp
            // fieldPos back onto the last real visible question rather than
            // pointing past the end of a now-shorter list.
            if(this.phase === 'field'){
                var vis = this.pagedFields
                if(vis.length && this.fieldPos >= vis.length){ this.fieldPos = vis.length - 1 }
            }
        },
        // Reflect a field's answer into every DOM element it backs in the live
        // preview, WITHOUT enforcing visibility (so rehydration can set all
        // controllers first, then clear hidden dependents in one final pass).
        // Round 13b: one writer for every question-pane -> preview push, and it
        // never writes into the focused control. syncAutoBindings() runs this on
        // every keystroke in the Vendor answer, which is exactly how a preview
        // edit was being silently overwritten.
        reflectField(f){
            if(!f.els) return
            mirrorFieldValue(f, f.value, (typeof document !== 'undefined') ? document.activeElement : null)
        },
        syncField(f){
            this.reflectField(f)
            this.enforceFieldVisibility()
            // Round 10: keep the document's conditional clauses in step with
            // the answers as they are given (see extractFields above).
            applyPreviewTriggers(this.$refs.doc)
            autoSizePreviewFields(this.$refs.doc)
            syncInlineEditors(this.$refs.doc)
        },
        acceptSuggest(f){ f.value = f.suggest; this.syncField(f) },
        // Item 4: counts only currently-visible fields - a question the person
        // was never asked (hidden by its controller) can never be "still
        // blank" and can never block Next or Review & send.
        countPlaceholders(){
            var n = 0
            // JW3 P0-A: checkbox fill-ins are optional (none is valid) and are
            // never counted as "still blank".
            this.visibleFields.forEach(function(f){ if(f.checkbox) return; if(!(f.value && String(f.value).trim())) n++ })
            this.placeholdersLeft = n
        },
        setPhaseForStep(){
            // Round 14: any movement between steps disarms the send.
            this.sendArmed = false
            var idx = this.stepIndex
            if(this.standardMode){
                var nAsk = this.askedFields.length
                // Round 7: step 0 is the Start (template choice) step.
                if(idx <= 0){ this.phase = 'method'; return }
                if(idx === 1){ this.phase = 'ba'; return }
                if(nAsk > 0 && idx >= 2 && idx <= 1 + nAsk){ this.phase = 'field'; this.fieldPos = idx - 2; this.focusField(); return }
                this.phase = (idx === 2 + nAsk) ? 'signerpass' : 'summary'
                return
            }
            if(idx === 0){ this.phase = 'method'; return }
            if(this.type === 'medcurity'){
                var base = (this.method === 'saved' || this.method === 'other' || this.templateAutoFailed) ? 1 : 0
                if(base === 1 && idx === 1){ this.phase = 'template'; return }
                var fStart = 1 + base
                // Joe (2026-08-14, guided-wizard resume-state integrity): the
                // "field" phase must always occupy at least one step slot, even
                // before any fields have been extracted yet (template just
                // chosen, with_fields HTML not yet parsed). Without the
                // Math.max floor, a momentary 0-length this.fields silently
                // shifted "ba" into the slot where "Details" belongs - the
                // step count still advanced, but nothing was ever actually
                // asked or validated there. Item 4: sized off visibleFields so
                // a trigger-hidden question (e.g. the termination date when
                // "No" is chosen) never occupies - or blocks Next through - a
                // step slot of its own.
                var fEnd = Math.max(fStart, base + this.visibleFields.length)
                if(idx >= fStart && idx <= fEnd){ this.phase = 'field'; this.fieldPos = idx - fStart; this.focusField(); return }
                var after = idx - fEnd
                this.phase = ['ba','signer','pass','renew','summary'][after-1] || 'summary'
                return
            }
            if(this.method === 'blank'){
                this.phase = ['title','body','ba','signer','pass','renew','summary'][idx-1] || 'summary'; return
            }
            this.phase = ['source','title','ba','signer','pass','renew','summary'][idx-1] || 'summary'
        },
        next(){
            if(this.phase === 'method'){
                this.type = (this.methods.find(m=>m.key===this.method)||{}).type
            }
            this.stepIndex++
            this.setPhaseForStep()
            this.$nextTick(this.scrollToQuestion)
        },
        back(){ if(this.stepIndex>0){ this.stepIndex--; this.setPhaseForStep(); this.$nextTick(this.scrollToQuestion) } },
        // Item 3: currentField.options renders a radiogroup <div> (not a single
        // focusable control) - focus its checked radio, or the first radio if
        // none is checked yet. Ordinary text/textarea/select fields keep
        // focusing $refs.fieldInput directly, unchanged.
        focusField(){
            var self = this
            this.$nextTick(function(){
                var el = self.$refs.fieldInput
                if(el){
                    if(typeof el.focus === 'function' && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT')){
                        el.focus()
                    } else if(el.querySelector){
                        var toFocus = el.querySelector('input[type=radio]:checked') || el.querySelector('input[type=radio]')
                        if(toFocus) toFocus.focus()
                    }
                }
                self.highlight()
            })
        },
        highlight(){
            var self=this
            // Item 4: fieldPos indexes visibleFields (not the raw fields
            // array), so the "is this the active question" check must compare
            // the field object itself rather than its position in this.fields.
            // A field currently hidden by its controller's answer gets no
            // highlight at all (its preview element is blank on purpose, not
            // "still needs an answer").
            var current = this.currentField
            this.fields.forEach(function(f){
                if(!f.els) return
                var visible = self.isFieldVisible(f)
                var isNow = visible && f === current
                var answered = f.checkbox ? (Array.isArray(f.value) && f.value.length > 0) : !!f.value
                f.els.forEach(function(e){
                    e.style.boxShadow = isNow ? '0 0 0 3px rgba(0,113,188,.35)' : ''
                    e.style.background = visible ? (answered?'#eff6ff':'#fef3c7') : ''
                })
            })
            // Center the active field WITHIN the preview panel only - never scroll the
            // page (side-by-side layout: the question card stays in view beside it). A
            // deduped field may back multiple DOM elements - the FIRST occurrence is
            // what gets scrolled into view.
            var el = this.currentField && this.currentField.els && this.currentField.els[0]
            var doc = this.$refs.doc
            if(el && doc && el.getBoundingClientRect){
                var er = el.getBoundingClientRect(), dr = doc.getBoundingClientRect()
                doc.scrollTop += (er.top - dr.top) - (doc.clientHeight / 2) + (er.height / 2)
            }
        },
        scrollToQuestion(){
            var f = this.$refs.form
            if(!f || !f.getBoundingClientRect) return
            var y = f.getBoundingClientRect().top + (window.pageYOffset || 0) - 88
            window.scrollTo(0, Math.max(0, y))
        },
        // JW3 P0-B (Joe, 2026-08-15): the ONE gate on transmission. The wizard's
        // submit IS create + send (store() acts on the send_on_save flag), so the
        // form may only ever POST from the explicit "Create and send BAA" button
        // on the Review (summary) step. Any other submit path - most importantly
        // an Enter keypress inside a text field on an earlier step - is cancelled
        // here, so Next, Back, refresh, and edit-mode navigation can never create
        // or email a BAA before the person reaches Step 6 and acts.
        // Round 14: the Review step's Send control, and nothing else, arms the
        // one submit this form is allowed to make.
        armSend(){
            this.sendArmed = true
        },
        onFormSubmit(e){
            if(this.phase !== 'summary' || this.placeholdersLeft > 0 || !this.sendArmed){
                e.preventDefault()
                this.sendArmed = false
            }
        },
    }
}
</script>

<style scoped>
.bgw-wrap{max-width:1320px;margin:0 auto;padding:6px 4px 48px;}
/* Round 7 (Joe req 2026-09-01): the guided wizard adopts the standard-BAA
   template builder's split (see .btb-split in BaaTemplateBuilder.vue):
   step/question pane LEFT, live document RIGHT and sticky, stacking on
   narrow viewports at the same 980px breakpoint the builder uses. */
.bgw-split{display:grid;grid-template-columns:minmax(320px,5fr) minmax(380px,7fr);gap:18px;align-items:start;}
.bgw-pane{min-width:0;}
.bgw-card{background:#fff;border:1px solid #e5e7eb;border-radius:16px;box-shadow:0 1px 2px rgba(16,24,40,.05);}
/* compact progress strip */
.bgw-prog{display:flex;align-items:center;gap:0;padding:6px 6px 20px;flex-wrap:wrap;}
.bgw-pdot{display:flex;align-items:center;gap:7px;}
.bgw-pnum{width:22px;height:22px;border-radius:50%;border:2px solid #e5e7eb;background:#fff;display:flex;align-items:center;justify-content:center;font-size:.7rem;font-weight:700;color:#6b7280;flex:0 0 auto;}
.bgw-pdot.done .bgw-pnum{background:#15803d;border-color:#15803d;color:#fff;}
.bgw-pdot.now .bgw-pnum{background:#0071BC;border-color:#0071BC;color:#fff;}
.bgw-plabel{font-size:.72rem;color:#6b7280;font-weight:600;white-space:nowrap;}
.bgw-pdot.now .bgw-plabel{color:#0b2545;}
.bgw-pbar{flex:1;height:2px;background:#e5e7eb;margin:0 8px;min-width:12px;}
.bgw-pbar.done{background:#15803d;}
/* question card */
.bgw-qcard{padding:22px 26px 20px;}
.bgw-count{font-size:.72rem;text-transform:uppercase;letter-spacing:.06em;color:#0071BC;font-weight:800;margin-bottom:10px;}
.bgw-qt{font-size:1.32rem;font-weight:800;color:#0b2545;margin-bottom:6px;letter-spacing:-.01em;}
.bgw-qh{color:#6b7280;font-size:.92rem;margin-bottom:18px;line-height:1.5;max-width:620px;}
.bgw-input{width:100%;max-width:540px;padding:12px 14px;border:1.5px solid #e5e7eb;border-radius:11px;font-size:1rem;background:#fff;color:#1f2a37;}
.bgw-input:focus{outline:none;border-color:#0071BC;box-shadow:0 0 0 3px rgba(0,113,188,.12);}
.bgw-area{max-width:none;min-height:150px;font-family:inherit;line-height:1.5;}
.bgw-suggest{margin-top:9px;font-size:.85rem;color:#6b7280;}
.bgw-suggest b{color:#15803d;}
.bgw-chip{display:inline-flex;align-items:center;gap:6px;background:#f0fdf4;color:#15803d;border:1px solid #bbf7d0;border-radius:999px;padding:3px 10px;font-weight:600;cursor:pointer;margin-left:6px;font-size:.82rem;}
.bgw-methods{display:grid;grid-template-columns:repeat(2,1fr);gap:12px;max-width:680px;}
.bgw-method{border:1.5px solid #e5e7eb;border-radius:14px;padding:15px;cursor:pointer;background:#fff;transition:.12s;}
.bgw-method:hover{border-color:#bfdbfe;background:#eff6ff;}
.bgw-method.sel{border-color:#0071BC;background:#eff6ff;box-shadow:0 0 0 3px rgba(0,113,188,.1);}
.bgw-mt{font-weight:700;color:#0b2545;margin-bottom:3px;}
.bgw-md{font-size:.84rem;color:#6b7280;line-height:1.45;}
.bgw-choices{display:flex;flex-direction:column;gap:10px;max-width:540px;}
.bgw-choice{border:1.5px solid #e5e7eb;border-radius:12px;padding:12px 16px;cursor:pointer;font-weight:600;color:#1f2a37;background:#fff;display:flex;align-items:center;gap:10px;}
.bgw-choice:hover{border-color:#bfdbfe;background:#eff6ff;}
.bgw-choice.sel{border-color:#0071BC;background:#eff6ff;}
.bgw-rr{width:18px;height:18px;border-radius:50%;border:2px solid #e5e7eb;flex:0 0 auto;}
.bgw-choice.sel .bgw-rr{border-color:#0071BC;background:#0071BC;box-shadow:inset 0 0 0 3px #fff;}
/* Item 3: question-card option list (replaces the native <select> dropdown).
   Real radio inputs, visually hidden but still focusable/keyboard-operable -
   arrow keys move between same-name radios natively, space/click selects.
   Full option text always shows and wraps (no clipped/truncated selection),
   styled like .bgw-method above. min-height 44px keeps a comfortable touch
   target down to 320px width. */
.bgw-options{display:flex;flex-direction:column;gap:10px;max-width:620px;}
.bgw-radio-opt{position:relative;display:flex;align-items:flex-start;gap:12px;min-height:44px;border:1.5px solid #e5e7eb;border-radius:12px;padding:12px 16px;cursor:pointer;background:#fff;font-weight:600;color:#1f2a37;line-height:1.45;transition:.12s;}
.bgw-radio-opt:hover{border-color:#bfdbfe;background:#eff6ff;}
.bgw-radio-opt.sel{border-color:#0071BC;background:#eff6ff;box-shadow:0 0 0 3px rgba(0,113,188,.1);}
.bgw-radio-input{position:absolute;opacity:0;width:1px;height:1px;margin:0;padding:0;}
.bgw-radio-input:focus-visible ~ .bgw-radio-mark{outline:2px solid #0071BC;outline-offset:2px;}
.bgw-radio-mark{width:18px;height:18px;border-radius:50%;border:2px solid #e5e7eb;flex:0 0 auto;margin-top:2px;}
.bgw-radio-opt.sel .bgw-radio-mark{border-color:#0071BC;background:#0071BC;box-shadow:inset 0 0 0 3px #fff;}
.bgw-radio-text{flex:1;white-space:normal;word-break:break-word;}
/* JW3 P0-A: multi-select checkbox cards - same full-width wrapping cards as the
   radio options, but a square mark that fills with a check when selected. */
.bgw-check-opt{position:relative;display:flex;align-items:flex-start;gap:12px;min-height:44px;border:1.5px solid #e5e7eb;border-radius:12px;padding:12px 16px;cursor:pointer;background:#fff;font-weight:600;color:#1f2a37;line-height:1.45;transition:.12s;}
.bgw-check-opt:hover{border-color:#bfdbfe;background:#eff6ff;}
.bgw-check-opt.sel{border-color:#0071BC;background:#eff6ff;box-shadow:0 0 0 3px rgba(0,113,188,.1);}
.bgw-check-mark{width:18px;height:18px;border-radius:5px;border:2px solid #e5e7eb;flex:0 0 auto;margin-top:2px;display:flex;align-items:center;justify-content:center;font-size:.72rem;color:transparent;line-height:1;}
.bgw-check-opt.sel .bgw-check-mark{border-color:#0071BC;background:#0071BC;color:#fff;}
.bgw-radio-input:focus-visible ~ .bgw-check-mark{outline:2px solid #0071BC;outline-offset:2px;}
/* JW3 P0-B: Review (summary) step */
.bgw-summary{border:1.5px solid #e5e7eb;border-radius:12px;overflow:hidden;max-width:680px;}
.bgw-srow{display:flex;gap:14px;padding:10px 16px;border-top:1px solid #eef1f4;align-items:baseline;}
.bgw-srow:first-child{border-top:none;}
.bgw-slabel{flex:0 0 190px;color:#6b7280;font-size:.82rem;font-weight:600;}
.bgw-sval{flex:1;color:#1f2a37;font-weight:600;word-break:break-word;}
.bgw-ssep{padding:8px 16px;background:#f8fafc;border-top:1px solid #eef1f4;font-size:.72rem;text-transform:uppercase;letter-spacing:.06em;color:#0071BC;font-weight:800;}
.bgw-summary-note{margin-top:12px;color:#6b7280;font-size:.86rem;max-width:620px;line-height:1.5;}
@media (max-width:560px){
    .bgw-srow{flex-direction:column;gap:2px;}
    .bgw-slabel{flex:none;}
}
@media (max-width:360px){
    .bgw-radio-opt,.bgw-check-opt{padding:12px;}
}
.bgw-nav{display:flex;align-items:center;margin-top:24px;}
.bgw-btn{border:none;border-radius:11px;padding:11px 20px;font-size:.95rem;font-weight:700;cursor:pointer;}
.bgw-btn.primary{background:#0b2545;color:#fff;}
/* Round 14: the send reads as the consequential action it is, never as another
   Next. Same family as the vendor page's own "Send for signature". */
.bgw-btn.send{background:#0b2545;color:#fff;box-shadow:0 1px 2px rgba(11,37,69,.28);}
.bgw-btn.send:hover{background:#14345c;}
.bgw-btn.send:disabled{background:#c7d0dc;cursor:not-allowed;box-shadow:none;}
.bgw-sendline{margin-top:16px;border:1px solid #fde68a;background:#fffbeb;border-radius:12px;padding:12px 14px;
    display:flex;flex-direction:column;gap:4px;max-width:680px;}
.bgw-sendlead{font-weight:800;color:#92400e;font-size:.9rem;}
.bgw-sendto{color:#5a4213;font-size:.86rem;line-height:1.5;}
.bgw-sendto b{color:#3f2d0a;}
.bgw-btn.primary:disabled{background:#c7d0dc;cursor:not-allowed;}
.bgw-btn.ghost{background:transparent;color:#6b7280;}
.bgw-btn.ghost:hover{color:#0b2545;}
.bgw-warn{margin-top:10px;color:#b45309;font-size:.85rem;font-weight:600;}
/* live document pane (right), sticky while the questions scroll */
.bgw-prevcard{position:sticky;top:12px;}
.bgw-prevhead{display:flex;align-items:center;justify-content:space-between;padding:14px 22px;border-bottom:1px solid #e5e7eb;}
.bgw-pt{font-weight:800;color:#0b2545;font-size:.95rem;}
.bgw-live{font-size:.7rem;font-weight:800;text-transform:uppercase;letter-spacing:.05em;color:#15803d;background:#f0fdf4;border:1px solid #bbf7d0;border-radius:999px;padding:2px 9px;}
.bgw-doc{padding:26px 34px 34px;font-size:.95rem;line-height:1.75;color:#374151;max-height:calc(100vh - 180px);overflow:auto;}
.bgw-doc >>> h1{font-size:1.3rem;font-weight:700;text-align:center;margin:0 0 1rem;color:#0b2545;}
.bgw-doc >>> h2{font-size:1.05rem;font-weight:700;margin:1.4rem 0 .4rem;color:#0b2545;}
/* Round 11 (Joe req 2026-09-01): the revealed fill-ins must be readable.
   policy-input.blade.php gives every `text` fill-in Bootstrap's .w-25
   (width:25% !important), which is about 119px in this pane, and textareas a
   fixed two rows. That server component is shared with company_policy/editor
   and PolicyMedcurityInputs.vue, so it is left alone and these overrides are
   confined to THIS preview pane by the .bgw-doc >>> prefix on every rule.
   autoSizePreviewFields() supplies the per-content size; these rules let it
   take effect and keep the control looking like an editable spot in the
   document rather than a form widget. */
.bgw-doc >>> input.form-control,
.bgw-doc >>> input[type="text"],
.bgw-doc >>> input[type="tel"]{width:auto !important;max-width:100%;min-width:12ch;display:inline-block;vertical-align:baseline;
    height:auto;padding:1px 6px;font-size:inherit;line-height:inherit;font-family:inherit;color:#0b2545;
    background:#f8fbff;border:0;border-bottom:1px dashed #9dc3e6;border-radius:3px;box-shadow:none;field-sizing:content;}
.bgw-doc >>> textarea.form-control{width:100%;max-width:100%;min-height:3.2em;height:auto;overflow:hidden;resize:vertical;
    display:block;margin:4px 0;padding:6px 8px;font-size:inherit;line-height:inherit;font-family:inherit;color:#0b2545;
    background:#f8fbff;border:1px solid #dbe7f3;border-radius:6px;box-shadow:none;field-sizing:content;}
.bgw-doc >>> input.form-control:focus,
.bgw-doc >>> textarea.form-control:focus{outline:2px solid rgba(0,113,188,.35);outline-offset:1px;background:#fff;}
.bgw-doc >>> select.form-control{width:auto !important;max-width:100%;display:inline-block;vertical-align:baseline;
    height:auto;padding:1px 22px 1px 6px;font-size:inherit;line-height:inherit;font-family:inherit;}
/* Round 12 (Joe req 2026-09-01): a sentence-continuation fill-in is an inline
   span, so it WRAPS across as many lines as the clause needs and reads as one
   continuous sentence with its lead-in. The real <input> stays in the DOM as
   the submit-path source of truth and is only visually hidden (never
   display:none, which applyPreviewTriggers owns on these ids). Scoped to this
   preview pane by the .bgw-doc >>> prefix, exactly as the round-11 rules are. */
.bgw-doc >>> .mc-inline-edit{display:inline;white-space:pre-wrap;overflow-wrap:anywhere;word-break:break-word;
    font:inherit;color:#0b2545;padding:0 2px;border-radius:2px;background:#f8fbff;
    border-bottom:1px dashed #9dc3e6;outline:none;cursor:text;}
.bgw-doc >>> .mc-inline-edit:focus{outline:2px solid rgba(0,113,188,.35);outline-offset:1px;background:#fff;}
.bgw-doc >>> .mc-inline-edit:empty::before{content:attr(data-placeholder);color:#9aa7b8;font-style:italic;}
.bgw-doc >>> .mc-shim-source{position:absolute !important;width:1px !important;height:1px !important;
    padding:0 !important;margin:-1px !important;overflow:hidden !important;clip:rect(0 0 0 0) !important;
    white-space:nowrap !important;border:0 !important;}
/* Round 13b (verification fix 2026-09-01): a preview control with no editable
   home is visibly not editable, rather than silently ignoring keystrokes. Muted
   ink on a flat ground, no dashed "type here" underline, and a default cursor.
   Scoped to this preview pane by the .bgw-doc >>> prefix, like every rule around it. */
.bgw-doc >>> input.mc-preview-readonly,
.bgw-doc >>> textarea.mc-preview-readonly{background:#f3f5f7;color:#5a6b80;border-bottom:1px solid #e0e5ea;
    cursor:default;font-style:normal;}
.bgw-doc >>> input.mc-preview-readonly:focus,
.bgw-doc >>> textarea.mc-preview-readonly:focus{outline:2px solid rgba(90,107,128,.25);outline-offset:1px;background:#f3f5f7;}
/* ── standard send + advanced "Edit this BAA" ─────────────────────────── */
.bgw-inh{display:block;font-size:.75rem;font-weight:600;color:#6b7280;margin-top:2px;}
.bgw-date{max-width:220px;display:inline-block;padding:8px 10px;}
.bgw-editlink{color:#0071BC;font-weight:700;text-decoration:none;font-size:.86rem;margin-left:6px;}
.bgw-editlink:hover{text-decoration:underline;color:#0b2545;}
/* The advanced "Edit this BAA" editor rides the same split as the wizard
   itself (Round 7), so it no longer needs a layout of its own. */
.bgw-advpanel{padding:18px 22px 22px;}
.bgw-advhead{display:flex;align-items:flex-start;justify-content:space-between;gap:14px;margin-bottom:8px;}
.bgw-advgroup{border-top:1px solid #eef1f4;padding:14px 0 4px;}
.bgw-advgt{font-size:.72rem;text-transform:uppercase;letter-spacing:.06em;color:#0071BC;font-weight:800;margin-bottom:10px;}
.bgw-advfield{margin-bottom:16px;}
.bgw-advlabel{font-weight:700;color:#0b2545;font-size:.9rem;margin-bottom:6px;line-height:1.45;}
.bgw-advfull{border-top:1px solid #eef1f4;padding-top:14px;margin-top:4px;}
.bgw-override{outline:none;min-height:300px;}
.bgw-override:focus{box-shadow:inset 0 0 0 2px rgba(0,113,188,.25);border-radius:8px;}
@media (max-width:980px){
    .bgw-split{display:block;}
    .bgw-prevcard{position:static;margin-top:18px;}
    .bgw-doc{max-height:560px;}
}
</style>
